import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PageResponse, PieceRegion } from '@/lib/types';

export type ModerationCandidate = {
  pieceId: string;
  prettyName: string;
  composerName: string | null;
  formsLabel: string;
  submitterHandle: string | null;
  submittedAt: string;
  status: 'PUBLISHED';
};

export type ClosestMatch = {
  pieceId: string;
  prettyName: string;
  composerName: string | null;
  similarity: number;
};

export type ModerationCandidatePiece = {
  pieceId: string;
  prettyName: string;
  canonicalName: string;
  composerId: string | null;
  composerName: string | null;
  forms: string[];
  region: PieceRegion | null;
  summary: string | null;
  aka: string[];
  version: number;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rejectionReason: string | null;
  mergedIntoPieceId: string | null;
  status: 'PUBLISHED';
};

export type ArrangementSummary = {
  arrangementId: string;
  prettyName: string;
  keyName: string | null;
};

export type SourceSummary = {
  pieceSourceId: string;
  kind: 'URL' | 'BOOK' | 'RECORDING' | 'YOUTUBE' | 'SPOTIFY';
  value: string;
  note: string | null;
};

export type ModerationCandidateDetail = {
  piece: ModerationCandidatePiece;
  closestMatches: ClosestMatch[];
  arrangements: ArrangementSummary[];
  sources: SourceSummary[];
};

export function useModerationCandidates(params: {
  page: number;
  size: number;
}): UseQueryResult<PageResponse<ModerationCandidate>> {
  return useQuery({
    queryKey: ['admin', 'moderation', 'candidates', params] as const,
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('page', String(params.page));
      sp.set('size', String(params.size));
      return api.fetch<PageResponse<ModerationCandidate>>(`/admin/moderation?${sp}`);
    },
  });
}

export function useModerationCandidate(
  pieceId: string,
): UseQueryResult<ModerationCandidateDetail> {
  return useQuery({
    queryKey: ['admin', 'moderation', 'candidate', pieceId] as const,
    queryFn: () => api.fetch<ModerationCandidateDetail>(`/admin/moderation/${pieceId}`),
    enabled: Boolean(pieceId),
  });
}

export type ApproveModerationCandidateVariables = {
  pieceId: string;
  expectedVersion: number;
};

export function useApproveModerationCandidate(): UseMutationResult<
  ModerationCandidatePiece,
  Error,
  ApproveModerationCandidateVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, expectedVersion }: ApproveModerationCandidateVariables) =>
      api.fetch<ModerationCandidatePiece>(`/admin/moderation/${pieceId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ expectedVersion }),
      }),
    onSuccess: (_piece, { pieceId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation', 'candidates'] });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'moderation', 'candidate', pieceId],
      });
    },
  });
}

export type RejectModerationCandidateVariables = {
  pieceId: string;
  expectedVersion: number;
  reason: string;
};

export function useRejectModerationCandidate(): UseMutationResult<
  ModerationCandidatePiece,
  Error,
  RejectModerationCandidateVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, expectedVersion, reason }: RejectModerationCandidateVariables) =>
      api.fetch<ModerationCandidatePiece>(`/admin/moderation/${pieceId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ expectedVersion, reason }),
      }),
    onSuccess: (_piece, { pieceId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation', 'candidates'] });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'moderation', 'candidate', pieceId],
      });
    },
  });
}

export type EditHandoffResult = {
  editorToken: string;
  redirectUrl: string;
};

export function useStartEditHandoff(): UseMutationResult<
  EditHandoffResult,
  Error,
  { pieceId: string }
> {
  return useMutation({
    mutationFn: ({ pieceId }: { pieceId: string }) =>
      api.fetch<EditHandoffResult>(`/admin/moderation/${pieceId}/edit-handoff`, {
        method: 'POST',
      }),
  });
}

export type MergeModerationCandidateVariables = {
  pieceId: string;
  expectedVersion: number;
  targetPieceId: string;
  transferredArrangementIds: string[];
  transferredSourceIds: string[];
};

export function useMergeModerationCandidate(): UseMutationResult<
  ModerationCandidatePiece,
  Error,
  MergeModerationCandidateVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      pieceId,
      expectedVersion,
      targetPieceId,
      transferredArrangementIds,
      transferredSourceIds,
    }: MergeModerationCandidateVariables) =>
      api.fetch<ModerationCandidatePiece>(`/admin/moderation/${pieceId}/merge`, {
        method: 'POST',
        body: JSON.stringify({
          expectedVersion,
          targetPieceId,
          transferredArrangementIds,
          transferredSourceIds,
        }),
      }),
    onSuccess: (_piece, { pieceId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'moderation', 'candidates'] });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'moderation', 'candidate', pieceId],
      });
    },
  });
}
